import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const DashboardPage = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cloningId, setCloningId] = useState(null);
  const [toast, setToast] = useState(null);
  const { getAuthHeader } = useContext(AuthContext);

  useEffect(() => {
    const fetchDatasets = async () => {
      try {
        setLoading(true);
        const config = {
          headers: getAuthHeader(),
        };

        const { data } = await axios.get('/api/datasets', config);
        setDatasets(data);
      } catch (error) {
        setError(
          error.response?.data?.message || 'Failed to load datasets. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDatasets();
  }, [getAuthHeader]);

  // Clone dataset handler
  const handleClone = async (datasetId) => {
    setCloningId(datasetId);
    setError(null);
    setToast(null);
    try {
      const config = { headers: getAuthHeader() };
      
      // Fetch the original dataset
      const { data: originalDataset } = await axios.get(`/api/datasets/${datasetId}`, config);
      
      // Create clone data with modified name
      const cloneData = {
        name: `${originalDataset.name} (Copy)`,
        description: originalDataset.description,
        rules: originalDataset.rules || [],
        destinations: originalDataset.destinations || []
      };
      
      // Create the new dataset
      await axios.post('/api/datasets', cloneData, config);
      
      setToast({ type: 'success', message: 'Dataset cloned successfully!' });
      
      // Refresh datasets list
      const { data } = await axios.get('/api/datasets', config);
      setDatasets(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clone dataset.');
    } finally {
      setCloningId(null);
    }
  };

  // Filter datasets based on search query
  const filteredDatasets = datasets.filter(dataset =>
    dataset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dataset.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Datasets</h1>
          <p className="text-gray-600">Create and manage your data enrichment pipelines</p>
        </div>

        <Link
          to="/datasets/create"
          className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Dataset
        </Link>
      </div>

      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search datasets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      {toast && (
        <div className={`mb-4 p-3 rounded-md text-white ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>{toast.message}</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-md p-4 mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredDatasets.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-gray-900 font-medium text-lg mb-1">No datasets</h3>
          <p className="text-gray-500 mb-6">Get started by creating a new dataset.</p>
          <Link
            to="/datasets/create"
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md transition-colors"
          >
            New Dataset
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDatasets.map((dataset) => (
            <Link
              key={dataset._id}
              to={`/datasets/${dataset._id}`}
              className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow group relative"
            >
              <div className="p-5">
                <div className="mb-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary-50">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                      <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                      <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                    </svg>
                  </span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">{dataset.name}</h3>
                {dataset.description && (
                  <p className="text-gray-500 text-sm line-clamp-2 mb-3">{dataset.description}</p>
                )}
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>{dataset.rules?.length || 0} rules</span>
                    <span>•</span>
                    <span>{dataset.destinations?.length || 0} destinations</span>
                  </div>
                  <span className="text-primary-600 text-sm font-medium">View details</span>
                </div>
                <button
                  type="button"
                  className="mt-4 inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-primary-50 text-primary-700 text-xs font-medium rounded transition-colors disabled:opacity-50"
                  onClick={e => { e.preventDefault(); handleClone(dataset._id); }}
                  disabled={cloningId === dataset._id}
                >
                  {cloningId === dataset._id ? (
                    <svg className="animate-spin h-4 w-4 mr-2 text-primary-600" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                  ) : (
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16v2a2 2 0 002 2h6a2 2 0 002-2V8a2 2 0 00-2-2h-2" /><rect width="8" height="8" x="8" y="8" rx="2" /></svg>
                  )}
                  {cloningId === dataset._id ? 'Cloning...' : 'Clone'}
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;